import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { getApiBaseUrl } from '@/lib/api'
import { getAuthHeaders } from '@/services/api'
import { useProgramContext } from '@/context/ProgramContext'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Dialog as ScreenshotDialog,
	DialogContent as ScreenshotDialogContent,
	DialogTrigger as ScreenshotDialogTrigger,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { formatDistanceToNow, format } from 'date-fns'
import {
	Globe,
	Laptop,
	Smartphone,
	Camera,
	X,
	AlertTriangle,
	Maximize2,
	CheckCircle,
	User,
	Settings,
	Calendar,
	Send,
	MessageCircle,
	Loader2,
	ChevronDown,
	ChevronUp,
	Sparkles,
	FileText,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { useTranslation } from '@/hooks/useTranslation'
import { useSendReplyShortcut } from '@/hooks/useSendReplyShortcut'
import { SendReplyShortcutHint } from '@/components/common/SendReplyShortcutHint'

interface UserMetadata {
	browser?: string
	browserVersion?: string
	os?: string
	osVersion?: string
	deviceVendor?: string
	deviceModel?: string
	deviceType?: string
	screenWidth?: number
	screenHeight?: number
	pageUrl?: string
	pageTitle?: string
	userAgent?: string
	parsedUA?: {
		os?: {
			name?: string
			version?: string
		}
		browser?: {
			name?: string
			version?: string
			major?: string
		}
		device?: {
			vendor?: string
			model?: string
			type?: string
		}
	}
}

interface ContactMessage {
	id: string
	description: string
	status: string
	severity: string
	widget: { name: string } | null
	program: { name: string } | null
	reporter_name: string | null
	reporter_email: string | null
	external_user_id: string | null
	team_id: string | null
	user_metadata: UserMetadata | null
	user_properties: Record<string, unknown> | null
	screenshot_url: string | null
	created_at: string
	updated_at: string
	assignee_user_id?: string | null
	widget_id?: string | null
	program_id?: string | null
	internal_notes?: string | null
}

interface ContactConversation {
	id: string
	contact_message_id: string
	sender_type: 'support' | 'customer'
	sender_user_id: string | null
	sender_name: string | null
	sender_email: string | null
	message: string
	created_at: string
	email_message_id: string | null
	in_reply_to: string | null
}

interface FeedbackHistory {
	id: string
	feedback_type: string
	feedback_id: string
	field_name: 'status' | 'severity' | 'internal_notes'
	old_value: string | null
	new_value: string
	changed_by_user_id: string | null
	changed_by_team_member_id: string | null
	created_at: string
	changed_by_name?: string
}

interface AiCitation {
	source: 'knowledge_doc' | 'previous_reply'
	title?: string
	excerpt?: string
}

interface AiSuggestion {
	draft: string
	confidence?: number
	citations?: AiCitation[]
	notes?: string
}

interface AiSuggestionResponse {
	suggestion: AiSuggestion
	eventId: string | null
	meta?: {
		knowledgeMatches: number
		previousReplyMatches: number
	}
}

interface AiAssistantStatus {
	enabled: boolean
	openAIConfigured: boolean
	docsReadyCount: number
	isPremium: boolean
}

interface KnowledgeDocument {
	id: string
	title: string
	file_name: string
	status: 'processing' | 'ready' | 'failed'
	chunk_count: number
	error_message?: string | null
	created_at: string
}

interface ContactMessageDetailViewProps {
	contactId: string | null
	onClose: () => void
}

export function ContactMessageDetailView({
	contactId,
	onClose,
}: ContactMessageDetailViewProps) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const { currentProgramAccessLevel } = useProgramContext()
	const [replyMessage, setReplyMessage] = useState('')
	const [isSendingReply, setIsSendingReply] = useState(false)
	const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false)
	const [aiSuggestion, setAiSuggestion] = useState<AiSuggestion | null>(null)
	const [aiSuggestionEventId, setAiSuggestionEventId] = useState<string | null>(null)
	const [aiSuggestionError, setAiSuggestionError] = useState<string | null>(null)
	const [showAiUpsell, setShowAiUpsell] = useState(false)
	const [appliedSuggestionDraft, setAppliedSuggestionDraft] = useState<string | null>(
		null
	)
	const [editedAiDraft, setEditedAiDraft] = useState('')
	const [selectedKnowledgeFile, setSelectedKnowledgeFile] = useState<File | null>(null)
	const [isUploadingKnowledge, setIsUploadingKnowledge] = useState(false)
	const [knowledgeUploadError, setKnowledgeUploadError] = useState<string | null>(null)
	const [internalNotes, setInternalNotes] = useState('')
	const [isInternalNotesExpanded, setIsInternalNotesExpanded] = useState(false)

	const getModalTitle = (contactData: ContactMessage | undefined) => {
		if (!contactData) return t('contactMessageDetailView.contactMessageDetails')
		const wordLimit = window.innerWidth >= 768 ? 12 : 7
		const shortDescription = contactData.description
			.split(' ')
			.slice(0, wordLimit)
			.join(' ')
		if (contactData.description.length > shortDescription.length) {
			return `${t('contactMessageDetailView.contactPrefix')} ${shortDescription}...`
		}
		return `${t('contactMessageDetailView.contactPrefix')} ${shortDescription}`
	}
	const isOpen = contactId !== null

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			onClose()
		}
	}

	const { data: contact, isLoading } = useQuery<ContactMessage>({
		queryKey: ['contact', contactId],
		queryFn: async () => {
			if (!contactId) throw new Error('Contact ID is required')
			const { data, error } = await supabase
				.from('contact_messages')
				.select('*, external_user_id, user_properties, widget:widget_id(name), program:program_id(name)')
				.eq('id', contactId)
				.single()

			if (error) {
				console.error('Error fetching contact details:', error)
				throw error
			}
			return data as ContactMessage
		},
		enabled: !!contactId,
	})

	useEffect(() => {
		if (contact) {
			setInternalNotes(contact.internal_notes || '')
			// Expand by default if internal notes are filled, collapse if empty
			setIsInternalNotesExpanded(!!contact.internal_notes)
		}
	}, [contact?.id]) // Only reset when contact ID changes, not on every update

	useEffect(() => {
		setAiSuggestion(null)
		setAiSuggestionEventId(null)
		setAiSuggestionError(null)
		setShowAiUpsell(false)
		setAppliedSuggestionDraft(null)
		setEditedAiDraft('')
	}, [contactId])

	useEffect(() => {
		if (!contact) return

		// Don't save if notes haven't changed
		if (internalNotes === (contact.internal_notes || '')) return

		const timer = setTimeout(() => {
			updateContactMutation.mutate({ internal_notes: internalNotes })
		}, 1000)

		return () => clearTimeout(timer)
	}, [internalNotes])

	const { data: conversations = [], isLoading: isLoadingConversations } = useQuery<ContactConversation[]>({
		queryKey: ['contact-conversations', contactId],
		queryFn: async () => {
			if (!contactId) throw new Error('Contact ID is required')
			const response = await fetch(`${getApiBaseUrl()}/api/contacts/protected/${contactId}/conversations`, {
				headers: await getAuthHeaders(),
			})

			if (!response.ok) {
				throw new Error('Failed to fetch conversations')
			}

			const data = await response.json()
			return data.conversations || []
		},
		enabled: !!contactId,
		refetchInterval: 30000,
	})

	const { data: history = [], isLoading: isLoadingHistory } = useQuery<FeedbackHistory[]>({
		queryKey: ['contact-history', contactId],
		queryFn: async () => {
			if (!contactId) throw new Error('Contact ID is required')
			const response = await fetch(`${getApiBaseUrl()}/api/contacts/protected/${contactId}/history`, {
				headers: await getAuthHeaders(),
			})

			if (!response.ok) {
				throw new Error('Failed to fetch history')
			}

			const data = await response.json()
			return data.history || []
		},
		enabled: !!contactId,
	})

	const { data: aiAssistantStatus } = useQuery<AiAssistantStatus>({
		queryKey: ['ai-assistant-status', contact?.team_id, contact?.program_id],
		queryFn: async () => {
			if (!contact?.team_id) {
				throw new Error('Team ID is required')
			}
			const params = new URLSearchParams({
				teamId: contact.team_id,
			})
			if (contact.program_id) {
				params.append('programId', contact.program_id)
			}

			const response = await fetch(
				`${getApiBaseUrl()}/api/ai-assistant/status?${params.toString()}`,
				{
					headers: await getAuthHeaders(),
				}
			)

			if (!response.ok) {
				throw new Error('Failed to fetch AI assistant status')
			}

			return response.json()
		},
		enabled: !!contact?.team_id,
		staleTime: 30000,
	})

	const { data: knowledgeDocsData, isLoading: isLoadingKnowledgeDocs } = useQuery<{
		documents: KnowledgeDocument[]
	}>({
		queryKey: ['ai-assistant-documents', contact?.team_id, contact?.program_id],
		queryFn: async () => {
			if (!contact?.team_id) {
				throw new Error('Team ID is required')
			}
			const params = new URLSearchParams({ teamId: contact.team_id })
			if (contact.program_id) {
				params.append('programId', contact.program_id)
			}
			const response = await fetch(
				`${getApiBaseUrl()}/api/ai-assistant/documents?${params.toString()}`,
				{
					headers: await getAuthHeaders(),
				}
			)
			if (!response.ok) {
				throw new Error('Failed to fetch knowledge documents')
			}
			return response.json()
		},
		enabled: !!contact?.team_id,
	})

	const hasKnowledgeDocuments = (knowledgeDocsData?.documents?.length ?? 0) > 0

	const canSendReply =
		!!replyMessage.trim() &&
		!isSendingReply &&
		currentProgramAccessLevel !== 'read'

	const canSendAiDraft =
		!!editedAiDraft.trim() &&
		!isSendingReply &&
		!!aiSuggestion &&
		currentProgramAccessLevel !== 'read'

	const { onKeyDown: onReplyKeyDown, shortcutLabel } = useSendReplyShortcut(
		handleSendReply,
		{ canSend: canSendReply, isSending: isSendingReply }
	)

	const { onKeyDown: onAiDraftKeyDown, shortcutLabel: aiDraftShortcutLabel } =
		useSendReplyShortcut(handleSendAiDraft, {
			canSend: canSendAiDraft,
			isSending: isSendingReply,
		})

	async function handleSendReply() {
		if (!contactId || !replyMessage.trim()) return

		setIsSendingReply(true)
		try {
			const wasEdited =
				appliedSuggestionDraft !== null &&
				replyMessage.trim() !== appliedSuggestionDraft.trim()

			const response = await fetch(`${getApiBaseUrl()}/api/contacts/protected/${contactId}/reply`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...await getAuthHeaders(),
				},
				body: JSON.stringify({
					message: replyMessage,
					aiSuggestionEventId: appliedSuggestionDraft
						? aiSuggestionEventId
						: null,
					aiEdited: wasEdited,
				}),
			})

			if (!response.ok) {
				const contentType = response.headers.get('content-type')
				let errorMessage = 'Failed to send reply'

				if (contentType && contentType.includes('application/json')) {
					try {
						const error = await response.json()
						errorMessage = error.error || errorMessage
					} catch (e) {
						console.error('Failed to parse error response:', e)
					}
				} else {
					const text = await response.text()
					console.error('Non-JSON error response:', text.substring(0, 200))
					errorMessage = `Server error (${response.status}): ${response.statusText}`
				}

				throw new Error(errorMessage)
			}

			const data = await response.json()
			setReplyMessage('')
			setAppliedSuggestionDraft(null)
			setAiSuggestionEventId(null)
			queryClient.invalidateQueries({ queryKey: ['contact-conversations', contactId] })
		} catch (error) {
			console.error('Error sending reply:', error)
			const message = error instanceof Error ? error.message : 'Failed to send reply. Please try again.'
			alert(message)
		} finally {
			setIsSendingReply(false)
		}
	}

	async function handleSendAiDraft() {
		if (!contactId || !editedAiDraft.trim() || !aiSuggestion) return

		setIsSendingReply(true)
		try {
			const wasEdited = editedAiDraft.trim() !== aiSuggestion.draft.trim()
			const response = await fetch(`${getApiBaseUrl()}/api/contacts/protected/${contactId}/reply`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...await getAuthHeaders(),
				},
				body: JSON.stringify({
					message: editedAiDraft,
					aiSuggestionEventId: aiSuggestionEventId,
					aiEdited: wasEdited,
				}),
			})
			if (!response.ok) {
				const contentType = response.headers.get('content-type')
				let errorMessage = 'Failed to send reply'
				if (contentType && contentType.includes('application/json')) {
					try {
						const error = await response.json()
						errorMessage = error.error || errorMessage
					} catch (e) {
						console.error('Failed to parse error response:', e)
					}
				}
				throw new Error(errorMessage)
			}
			setReplyMessage('')
			setAppliedSuggestionDraft(null)
			setAiSuggestionEventId(null)
			setAiSuggestion(null)
			setEditedAiDraft('')
			queryClient.invalidateQueries({ queryKey: ['contact-conversations', contactId] })
		} catch (error) {
			console.error('Error sending reply:', error)
			const msg = error instanceof Error ? error.message : 'Failed to send reply. Please try again.'
			alert(msg)
		} finally {
			setIsSendingReply(false)
		}
	}

	const handleGenerateSuggestion = async () => {
		if (!contactId) return

		setIsGeneratingSuggestion(true)
		setAiSuggestionError(null)
		setShowAiUpsell(false)

		try {
			const response = await fetch(
				`${getApiBaseUrl()}/api/ai-assistant/contacts/${contactId}/suggest-reply`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						...await getAuthHeaders(),
					},
					body: JSON.stringify({}),
				}
			)

			if (!response.ok) {
				const errorBody = await response.json().catch(() => ({}))
				if (
					response.status === 402 ||
					errorBody?.code === 'ai_reply_assistant_required'
				) {
					setShowAiUpsell(true)
					return
				}
				throw new Error(errorBody?.error || 'Failed to generate AI suggestion')
			}

			const data: AiSuggestionResponse = await response.json()
			setAiSuggestion(data.suggestion)
			setAiSuggestionEventId(data.eventId)
			setEditedAiDraft(data.suggestion.draft)
		} catch (error) {
			setAiSuggestionError(
				error instanceof Error ? error.message : 'Failed to generate AI suggestion'
			)
		} finally {
			setIsGeneratingSuggestion(false)
		}
	}

	const handleGenerateSuggestionClick = () => {
		if (!aiAssistantStatus?.enabled) {
			setShowAiUpsell(true)
			setAiSuggestionError(null)
			return
		}

		if (!aiAssistantStatus?.openAIConfigured) {
			setAiSuggestionError(
				t(
					'contactMessageDetailView.openAiNotConfigured',
					'OpenAI is not configured for this environment. Set OPENAI_API_KEY in backend env and restart the backend.'
				)
			)
			return
		}

		handleGenerateSuggestion()
	}

	const handleUploadKnowledgeDoc = async () => {
		if (!selectedKnowledgeFile || !contact?.team_id) return

		setIsUploadingKnowledge(true)
		setKnowledgeUploadError(null)

		try {
			const formData = new FormData()
			formData.append('file', selectedKnowledgeFile)
			formData.append('teamId', contact.team_id)
			if (contact.program_id) {
				formData.append('programId', contact.program_id)
			}

			const {
				data: { session },
			} = await supabase.auth.getSession()
			const response = await fetch(
				`${getApiBaseUrl()}/api/ai-assistant/documents/upload`,
				{
					method: 'POST',
					headers: session?.access_token
						? { Authorization: `Bearer ${session.access_token}` }
						: undefined,
					body: formData,
				}
			)

			if (!response.ok) {
				const errorBody = await response.json().catch(() => ({}))
				throw new Error(errorBody?.error || 'Failed to upload document')
			}

			setSelectedKnowledgeFile(null)
			queryClient.invalidateQueries({
				queryKey: ['ai-assistant-documents', contact.team_id, contact.program_id],
			})
			queryClient.invalidateQueries({
				queryKey: ['ai-assistant-status', contact.team_id, contact.program_id],
			})
		} catch (error) {
			setKnowledgeUploadError(
				error instanceof Error ? error.message : 'Failed to upload document'
			)
		} finally {
			setIsUploadingKnowledge(false)
		}
	}

	const handleDeleteKnowledgeDoc = async (documentId: string) => {
		if (!contact?.team_id) return
		try {
			const params = new URLSearchParams({
				teamId: contact.team_id,
			})
			const response = await fetch(
				`${getApiBaseUrl()}/api/ai-assistant/documents/${documentId}?${params.toString()}`,
				{
					method: 'DELETE',
					headers: await getAuthHeaders(),
				}
			)

			if (!response.ok) {
				const errorBody = await response.json().catch(() => ({}))
				throw new Error(errorBody?.error || 'Failed to delete document')
			}

			queryClient.invalidateQueries({
				queryKey: ['ai-assistant-documents', contact.team_id, contact.program_id],
			})
			queryClient.invalidateQueries({
				queryKey: ['ai-assistant-status', contact.team_id, contact.program_id],
			})
		} catch (error) {
			setKnowledgeUploadError(
				error instanceof Error ? error.message : 'Failed to delete document'
			)
		}
	}

	const updateContactMutation = useMutation({
		mutationFn: async (data: Partial<ContactMessage>) => {
			if (!contactId) throw new Error('Contact ID is required for update')

			// Use the protected API endpoint with proper access control
			const response = await fetch(`${getApiBaseUrl()}/api/contacts/protected/${contactId}`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					...await getAuthHeaders(),
				},
				body: JSON.stringify(data),
			})

			if (!response.ok) {
				const error = await response.json()
				throw new Error(error.error || 'Failed to update contact report')
			}

			return response.json()
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['contact', contactId] })
			queryClient.invalidateQueries({ queryKey: ['contacts'] })
			queryClient.invalidateQueries({ queryKey: ['dashboardData'] })
			queryClient.invalidateQueries({ queryKey: ['contact-history', contactId] })
			queryClient.invalidateQueries({
				queryKey: ['recentWidgetData', contact?.widget_id, contact?.program_id, 'contacts'],
			})
		},
	})

	if (isLoading || (isOpen && !contact)) {
		return (
			<Dialog open={isOpen} onOpenChange={handleOpenChange}>
				<DialogContent className="sm:max-w-xl md:max-w-2xl overflow-y-auto max-h-[90vh]">
					<DialogHeader>
						<Skeleton className="h-6 w-3/4" />
					</DialogHeader>
					<div className="space-y-6 py-4">
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-32 w-full" />
						<Skeleton className="h-20 w-full" />
						<Skeleton className="h-20 w-full" />
					</div>
				</DialogContent>
			</Dialog>
		)
	}

	if (!isOpen || !contact) return null

	const isAiEnabled = Boolean(aiAssistantStatus?.enabled)
	const isOpenAiConfigured = Boolean(aiAssistantStatus?.openAIConfigured)
	const isAiReady = isAiEnabled && isOpenAiConfigured

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent
				aria-describedby={contact ? `contact-report-description-${contact.id}` : undefined}
				className="sm:max-w-2xl md:max-w-4xl lg:max-w-6xl overflow-y-auto max-h-[90vh]">
				<DialogHeader className="space-y-2 pr-8">
					<div className="flex items-center justify-between">
						<DialogTitle
							className="text-xl font-semibold truncate"
							title={contact.description}>
							🐛 {getModalTitle(contact)}
						</DialogTitle>
					</div>
					<VisuallyHidden>
						<DialogDescription
							id={contact ? `contact-report-description-${contact.id}` : undefined}>
							{t('contactMessageDetailView.detailsForContactMessage')} #{contact.id.slice(0, 8)}. {t('contactMessageTable.status')}: {contact.status}
							, {t('contactMessageTable.severity')}: {contact.severity}.
						</DialogDescription>
					</VisuallyHidden>
					<div className="flex gap-3 pt-2">
						<div className="flex items-center gap-2">
							<label className="text-xs font-medium text-muted-foreground">{t('contactMessageTable.status')}:</label>
							<Select
								value={contact.status}
								onValueChange={(value) =>
									updateContactMutation.mutate({
										status: value,
									})
								}
								disabled={currentProgramAccessLevel === 'read'}>
								<SelectTrigger className={`w-32 h-7 text-xs ${contact.status === 'new'
										? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-800'
										: contact.status === 'in_progress'
											? 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:border-yellow-800'
											: contact.status === 'resolved' || contact.status === 'closed'
												? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900 dark:text-green-300 dark:border-green-800'
												: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
									}`}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="new">{t('contactMessageTable.statusNew')}</SelectItem>
									<SelectItem value="in_progress">{t('contactMessageTable.statusInProgress')}</SelectItem>
									<SelectItem value="resolved">{t('contactMessageTable.statusResolved')}</SelectItem>
									<SelectItem value="closed">{t('contactMessageTable.statusClosed')}</SelectItem>
									<SelectItem value="archived">{t('contactMessageTable.statusArchived')}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="flex items-center gap-2">
							<label className="text-xs font-medium text-muted-foreground">{t('contactMessageTable.severity')}:</label>
							<Select
								value={contact.severity}
								onValueChange={(value) =>
									updateContactMutation.mutate({
										severity: value,
									})
								}
								disabled={currentProgramAccessLevel === 'read'}>
								<SelectTrigger className={`w-24 h-7 text-xs ${contact.severity === 'critical'
										? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900 dark:text-red-300 dark:border-red-800'
										: contact.severity === 'high'
											? 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900 dark:text-orange-300 dark:border-orange-800'
											: contact.severity === 'medium'
												? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900 dark:text-amber-300 dark:border-amber-800'
												: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
									}`}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="critical">{t('contactMessageTable.severityCritical')}</SelectItem>
									<SelectItem value="high">{t('contactMessageTable.severityHigh')}</SelectItem>
									<SelectItem value="medium">{t('contactMessageTable.severityMedium')}</SelectItem>
									<SelectItem value="low">{t('contactMessageTable.severityLow')}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{contact.widget?.name && (
							<Badge variant="outline">{contact.widget.name}</Badge>
						)}
					</div>
				</DialogHeader>

				<Separator className="my-2" />

				<div className="space-y-6">
					<div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
						{/* Main Content - Left Column */}
						<div className="lg:col-span-8 space-y-6">


							<div className="space-y-3">
								<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
									<div className="h-4 w-4 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
										<svg className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
										</svg>
									</div>
									{t('contactMessageTable.description')}
								</h3>
								<div className="bg-muted/50 p-4 rounded-lg min-h-[120px] whitespace-pre-wrap text-sm">
									{contact.description}
								</div>
							</div>
							{/* Internal Notes */}
							<div className="space-y-3">
								<button
									type="button"
									onClick={() => setIsInternalNotesExpanded(!isInternalNotesExpanded)}
									className="text-sm font-medium text-muted-foreground flex items-center justify-between w-full hover:text-foreground transition-colors"
								>
									<div className="flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-tertiary/80 rounded-full">
											<Settings className="h-2.5 w-2.5 text-primary" />
										</div>
										{t('contactMessageDetailView.internalNotes', 'Internal Notes')}
									</div>
									<div className="flex items-center gap-2">
										{updateContactMutation.isPending && (
											<div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
												<Loader2 className="h-2.5 w-2.5 animate-spin" />
												{t('contactMessageDetailView.saving', 'Saving...')}
											</div>
										)}
										{!updateContactMutation.isPending && updateContactMutation.isSuccess && (
											<div className="flex items-center gap-1.5 text-[10px] text-emerald-500 font-medium">
												<CheckCircle className="h-2.5 w-2.5" />
												{t('contactMessageDetailView.saved', 'Saved')}
											</div>
										)}
										{isInternalNotesExpanded ? (
											<ChevronUp className="h-4 w-4" />
										) : (
											<ChevronDown className="h-4 w-4" />
										)}
									</div>
								</button>
								{isInternalNotesExpanded && (
									<div className="space-y-2">
										<Textarea
											value={internalNotes}
											onChange={(e) => setInternalNotes(e.target.value)}
											placeholder={t('contactMessageDetailView.internalNotesPlaceholder', 'Add private context or updates for your team...')}
											className="min-h-[100px] text-sm bg-muted/50 focus:bg-background transition-colors"
											disabled={currentProgramAccessLevel === 'read'}
										/>
										<p className="text-[10px] text-muted-foreground italic px-1">
											{t('contactMessageDetailView.internalNotesHint', 'Notes are private to your team and saved automatically as you type.')}
										</p>
									</div>
								)}
							</div>

							{contact.reporter_email && (
								<div className="space-y-3">
									<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-purple-100 dark:bg-purple-900 rounded-full">
											<MessageCircle className="h-2.5 w-2.5 text-purple-600 dark:text-purple-400" />
										</div>
										{t('contactMessageDetailView.conversation', 'Conversation')}
									</h3>

									<div className="space-y-3">
										{isLoadingConversations ? (
											<div className="flex justify-center py-4">
												<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
											</div>
										) : conversations.length > 0 ? (
											<div className="space-y-3">
												{conversations.map((conv) => (
													<Card key={conv.id} className={`p-4 ${conv.sender_type === 'support' ? 'bg-blue-50 dark:bg-blue-950/20' : 'bg-muted/50'}`}>
														<div className="flex items-start gap-3">
															<div className={`h-8 w-8 rounded-full flex items-center justify-center ${conv.sender_type === 'support' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-200 dark:bg-gray-700'}`}>
																{conv.sender_type === 'support' ? (
																	<User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
																) : (
																	<User className="h-4 w-4 text-gray-600 dark:text-gray-400" />
																)}
															</div>
															<div className="flex-1 space-y-1">
																<div className="flex items-center justify-between">
																	<span className="text-sm font-medium">
																		{conv.sender_name || (conv.sender_type === 'support' ? 'Support' : 'Customer')}
																	</span>
																	<span className="text-xs text-muted-foreground">
																		{formatDistanceToNow(new Date(conv.created_at), { addSuffix: true })}
																	</span>
																</div>
																<p className="text-sm whitespace-pre-wrap">{conv.message}</p>
															</div>
														</div>
													</Card>
												))}
											</div>
										) : (
											<div className="text-center py-6 text-sm text-muted-foreground">
												{t('contactMessageDetailView.noConversation', 'No messages yet. Send a reply to start the conversation.')}
											</div>
										)}
									</div>

										{currentProgramAccessLevel !== 'read' && (
											<div className="space-y-4">
												<div className="rounded-xl border bg-gradient-to-br from-primary/5 via-background to-background p-4 space-y-3">
													<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
														<div className="space-y-2">
															<div className="flex items-center gap-2">
																<div className="h-7 w-7 rounded-md bg-primary/10 text-primary flex items-center justify-center">
																	<Sparkles className="h-4 w-4" />
																</div>
																<p className="text-sm font-semibold">
																	{t(
																		'contactMessageDetailView.aiAssistantTitle',
																		'AI Reply Assistant'
																	)}
																</p>
															</div>
															<p className="text-xs text-muted-foreground">
																{t(
																	'contactMessageDetailView.aiHint',
																	'Generate a ready-to-send draft from past replies and uploaded docs.'
																)}
															</p>
														</div>
														<Button
															type="button"
															variant={isAiReady ? 'default' : 'secondary'}
															size="sm"
															className="sm:self-center"
															onClick={handleGenerateSuggestionClick}
															disabled={isGeneratingSuggestion}
														>
															{isGeneratingSuggestion ? (
																<Loader2 className="h-4 w-4 mr-2 animate-spin" />
															) : (
																<Sparkles className="h-4 w-4 mr-2" />
															)}
															{t(
																'contactMessageDetailView.generateDraft',
																'Generate Draft'
															)}
														</Button>
													</div>

													{!isAiReady && (
														<div className="rounded-lg border border-muted bg-muted/30 px-3 py-2 text-xs text-muted-foreground flex items-start gap-2">
															<AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
															<p>
																{t(
																	'contactMessageDetailView.aiUnavailableSimple',
																	'Draft generation is currently unavailable.'
																)}
															</p>
														</div>
													)}

													{showAiUpsell && (
														<div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
															<p>
																{t(
																	'contactMessageDetailView.aiUpsell',
																	'AI Reply Assistant is an add-on (+$15/team). Enable it in Billing to generate drafts.'
																)}{' '}
																<button
																	type="button"
																	className="underline font-medium underline-offset-2"
																	onClick={() =>
																		(window.location.href = '/settings?tab=billing')
																	}
																>
																	{t(
																		'contactMessageDetailView.openBilling',
																		'Open Billing'
																	)}
																</button>
															</p>
														</div>
													)}

													{aiSuggestionError && (
														<div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-start gap-2">
															<AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
															<p>
																{t(
																	'contactMessageDetailView.aiDraftErrorSimple',
																	'Could not generate a draft right now. Please try again.'
																)}
															</p>
														</div>
													)}

													{aiSuggestion && (
														<div className="rounded-xl border border-primary/20 bg-background p-4 space-y-3 shadow-sm">
															<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
																<div className="flex flex-wrap items-center gap-2">
																	<p className="text-sm font-semibold">
																		{t(
																			'contactMessageDetailView.aiDraft',
																			'Suggested Draft'
																		)}
																	</p>
																</div>
																<div className="flex items-center gap-2">
																	<Button
																		type="button"
																		variant="outline"
																		size="sm"
																		onClick={handleGenerateSuggestion}
																		disabled={isGeneratingSuggestion}
																	>
																		{isGeneratingSuggestion && (
																			<Loader2 className="h-4 w-4 mr-2 animate-spin" />
																		)}
																		{t(
																			'contactMessageDetailView.regenerate',
																			'Regenerate'
																		)}
																	</Button>
																</div>
															</div>
															<textarea
																className="w-full rounded-lg border bg-muted/30 p-3 text-sm leading-6 resize-y min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
																value={editedAiDraft}
																onChange={(e) => setEditedAiDraft(e.target.value)}
																onKeyDown={onAiDraftKeyDown}
																disabled={isSendingReply}
															/>
															<div className="flex justify-end">
																<Button
																	type="button"
																	variant="default"
																	size="sm"
																	onClick={handleSendAiDraft}
																	disabled={!editedAiDraft.trim() || isSendingReply}
																>
																	{isSendingReply && (
																		<Loader2 className="h-4 w-4 mr-2 animate-spin" />
																	)}
																	{isSendingReply ? t('contactMessageDetailView.sending', 'Sending...') : t('contactMessageDetailView.sendReply', 'Send Reply')}
																	{!isSendingReply && <SendReplyShortcutHint label={aiDraftShortcutLabel} />}
																</Button>
															</div>
															{(aiSuggestion.citations || []).length > 0 && (
																<div className="space-y-2 border-t pt-3">
																	<p className="text-xs font-medium text-muted-foreground">
																		{t(
																			'contactMessageDetailView.sources',
																			'Sources'
																		)}
																	</p>
																	<div className="space-y-2">
																		{(aiSuggestion.citations || []).map((citation, idx) => (
																			<div
																				key={`${citation.title || 'source'}-${idx}`}
																				className="rounded-md border bg-muted/20 px-3 py-2 text-xs"
																			>
																				<p className="font-medium">
																					{citation.title ||
																						(citation.source === 'knowledge_doc'
																							? t(
																									'contactMessageDetailView.knowledgeDoc',
																									'Knowledge Doc'
																							  )
																							: t(
																									'contactMessageDetailView.previousReply',
																									'Previous Reply'
																							  ))}
																				</p>
																				{citation.excerpt && (
																					<p className="text-muted-foreground mt-1.5">
																						{citation.excerpt}
																					</p>
																				)}
																			</div>
																		))}
																	</div>
																</div>
															)}
														</div>
													)}
												</div>

													{!hasKnowledgeDocuments && (
														<div className="rounded-xl border bg-background p-4 space-y-3">
														<div className="flex items-center gap-2">
															<div className="h-7 w-7 rounded-md bg-muted text-muted-foreground flex items-center justify-center">
																<FileText className="h-4 w-4" />
															</div>
															<div>
																<p className="text-sm font-semibold">
																	{t(
																		'contactMessageDetailView.knowledgeDocs',
																		'Knowledge Documents'
																	)}
																</p>
																<p className="text-[11px] text-muted-foreground">
																	{t(
																		'contactMessageDetailView.knowledgeDocsHint',
																		'Upload docs to improve response quality.'
																	)}
																</p>
															</div>
														</div>

													<div className="rounded-lg border border-dashed bg-muted/20 p-3 space-y-2">
														<div className="flex flex-col sm:flex-row gap-2">
															<Input
																type="file"
																accept=".pdf,.txt,.md,.markdown,.html,.htm,text/plain,text/markdown,text/html,application/pdf"
																onChange={(e) =>
																	setSelectedKnowledgeFile(
																		e.target.files?.[0] || null
																	)
																}
																className="text-xs bg-background"
															/>
															<Button
																type="button"
																variant="outline"
																size="sm"
																onClick={handleUploadKnowledgeDoc}
																disabled={
																	!selectedKnowledgeFile || isUploadingKnowledge
																}
															>
																{isUploadingKnowledge ? (
																	<Loader2 className="h-4 w-4 mr-2 animate-spin" />
																) : null}
																{t(
																	'contactMessageDetailView.uploadDoc',
																	'Upload'
																)}
															</Button>
														</div>
														{selectedKnowledgeFile && (
															<div className="flex items-center justify-between rounded-md border bg-background px-2 py-1.5">
																<p className="truncate text-[11px] text-muted-foreground">
																	{selectedKnowledgeFile.name}
																</p>
																<Button
																	type="button"
																	variant="ghost"
																	size="sm"
																	className="h-6 px-2 text-[11px]"
																	onClick={() => setSelectedKnowledgeFile(null)}
																>
																	{t('common.clear', 'Clear')}
																</Button>
															</div>
														)}
													</div>

													{knowledgeUploadError && (
														<div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
															{t(
																'contactMessageDetailView.uploadErrorSimple',
																'Could not upload this file right now. Please try again.'
															)}
														</div>
													)}

													<div className="space-y-2 max-h-48 overflow-auto pr-1">
														{isLoadingKnowledgeDocs ? (
															<div className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
																{t(
																	'contactMessageDetailView.loadingDocs',
																	'Loading documents...'
																)}
															</div>
														) : (knowledgeDocsData?.documents || []).length === 0 ? (
															<div className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
																{t(
																	'contactMessageDetailView.noDocs',
																	'No documents uploaded yet.'
																)}
															</div>
														) : (
															(knowledgeDocsData?.documents || []).map((doc) => (
																<div
																	key={doc.id}
																	className="flex items-start justify-between gap-3 rounded-md border bg-muted/20 px-3 py-2"
																>
																	<div className="min-w-0 flex-1">
																		<p className="text-xs font-medium truncate">
																			{doc.file_name}
																		</p>
																	</div>
																	<Button
																		type="button"
																		variant="ghost"
																		size="sm"
																		className="h-7 px-2 text-xs"
																		onClick={() => handleDeleteKnowledgeDoc(doc.id)}
																	>
																		{t(
																			'contactMessageDetailView.removeDoc',
																			'Remove'
																		)}
																	</Button>
																</div>
															))
														)}
													</div>
												</div>
													)}

											<Textarea
												placeholder={t('contactMessageDetailView.typeReply', 'Type your reply...')}
												value={replyMessage}
												onChange={(e) => setReplyMessage(e.target.value)}
												onKeyDown={onReplyKeyDown}
												className="min-h-[100px]"
												disabled={isSendingReply}
											/>
											<div className="flex justify-end">
												<Button
													onClick={handleSendReply}
													disabled={!replyMessage.trim() || isSendingReply}
													size="sm"
												>
													<Send className="h-4 w-4 mr-2" />
													{isSendingReply ? t('contactMessageDetailView.sending', 'Sending...') : t('contactMessageDetailView.sendReply', 'Send Reply')}
													{!isSendingReply && <SendReplyShortcutHint label={shortcutLabel} />}
												</Button>
											</div>
										</div>
									)}
								</div>
							)}

							{history.length > 0 && (
								<div className="space-y-3 mt-6">
									<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
											<Calendar className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
										</div>
										{t('contactMessageDetailView.history', 'History')}
									</h3>
									<div className="bg-muted/50 p-4 rounded-lg space-y-3">
										{isLoadingHistory ? (
											<div className="flex justify-center py-4">
												<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
											</div>
										) : (
											history.map((item) => (
												<div key={item.id} className="flex items-start gap-3 pb-3 border-b last:border-b-0 last:pb-0">
													<div className="h-8 w-8 rounded-full flex items-center justify-center bg-blue-100 dark:bg-blue-900 flex-shrink-0">
														<Settings className="h-4 w-4 text-blue-600 dark:text-blue-400" />
													</div>
													<div className="flex-1 space-y-1">
														<div className="flex items-center justify-between">
															<span className="text-sm font-medium">
																{item.field_name === 'status'
																	? t('contactMessageDetailView.statusChanged', 'Status changed')
																	: item.field_name === 'severity'
																		? t('contactMessageDetailView.severityChanged', 'Severity changed')
																		: t('contactMessageDetailView.internalNotesUpdated', 'Internal notes updated')}
															</span>
															<span className="text-xs text-muted-foreground">
																{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
															</span>
														</div>
														<div className="text-sm">
															<span className="text-muted-foreground">
																{item.old_value || t('contactMessageDetailView.none', 'None')}
															</span>
															<span className="mx-2">→</span>
															<span className="font-medium">{item.new_value}</span>
														</div>
														{item.changed_by_name && (
															<div className="text-xs text-muted-foreground">
																{t('contactMessageDetailView.changedBy', 'Changed by')} {item.changed_by_name}
															</div>
														)}
													</div>
												</div>
											))
										)}
									</div>
								</div>
							)}
						</div>


						{/* Sidebar - Right Column with vertical separator */}
						<div className="lg:col-span-4 lg:border-l lg:border-border lg:pl-6 space-y-6">
							{contact.screenshot_url && (
								<div className="space-y-3">
									<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-purple-100 dark:bg-purple-900 rounded-full">
											<Camera className="h-2.5 w-2.5 text-purple-600 dark:text-purple-400" />
										</div>
										{t('contactMessageTable.screenshot')}
									</h3>
									<ScreenshotDialog>
										<ScreenshotDialogTrigger asChild>
											<div className="relative aspect-video rounded-lg border overflow-hidden cursor-pointer group bg-muted">
												<img
													src={contact.screenshot_url}
													alt={t('contactMessageDetailView.contactMessageScreenshot')}
													className="object-contain w-full h-full transition-transform group-hover:scale-[1.02]"
												/>
												<div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
													<Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-75 transition-opacity" />
												</div>
											</div>
										</ScreenshotDialogTrigger>
										<ScreenshotDialogContent className="max-w-[90vw] max-h-[90vh] w-screen h-screen p-0 bg-black/95 border-none shadow-none flex items-center justify-center [&_button_svg]:text-white [&_button]:absolute [&_button]:top-4 [&_button]:right-4 [&_button]:z-50">
											<img
												src={contact.screenshot_url}
												alt={t('contactMessageDetailView.contactMessageScreenshot')}
												className="max-w-full max-h-full object-contain"
											/>
										</ScreenshotDialogContent>
									</ScreenshotDialog>
								</div>
							)}

							<Separator className="my-2" />

							<div className="space-y-3">
								<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
									<div className="h-4 w-4 flex items-center justify-center bg-emerald-100 dark:bg-emerald-900 rounded-full">
										<User className="h-2.5 w-2.5 text-emerald-600 dark:text-emerald-400" />
									</div>
									{t('contactMessageDetailView.reporterDetails')}
								</h3>
								<div className="bg-muted/50 p-4 rounded-lg space-y-3">
									<div className="flex justify-between items-center">
										<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
											<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
												<User className="h-2 w-2 text-blue-600 dark:text-blue-400" />
											</div>
											{t('contactMessageDetailView.name')}:
										</span>
										<div className="flex items-center gap-2">
											<span className="text-xs text-foreground font-medium">
												{contact.reporter_name || t('contactMessageTable.anonymous')}
											</span>
											{contact.external_user_id && (
												<Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 text-xs">
													<CheckCircle className="h-3 w-3 mr-1" />
													{t('contactMessageDetailView.verified')}
												</Badge>
											)}
										</div>
									</div>

									{contact.reporter_email && (
										<div className="flex justify-between items-center">
											<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
												<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
													<svg className="h-2 w-2 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
													</svg>
												</div>
												{t('contactMessageDetailView.email')}:
											</span>
											<a
												href={`mailto:${contact.reporter_email}`}
												className="text-xs text-foreground font-medium hover:text-primary transition-colors">
												{contact.reporter_email}
											</a>
										</div>
									)}

									<div className="flex justify-between items-center">
										<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
											<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
												<Calendar className="h-2 w-2 text-blue-600 dark:text-blue-400" />
											</div>
											{t('contactMessageDetailView.created')}:
										</span>
										<span className="text-xs text-foreground font-medium">
											{format(new Date(contact.created_at), 'MMM d, yyyy')} {t('contactMessageDetailView.at')} {format(new Date(contact.created_at), 'h:mm a')}
										</span>
									</div>

									<div className="flex justify-between items-center">
										<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
											<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
												<Calendar className="h-2 w-2 text-blue-600 dark:text-blue-400" />
											</div>
											{t('contactMessageDetailView.updated')}:
										</span>
										<span className="text-xs text-foreground font-medium">
											{format(new Date(contact.updated_at), 'MMM d, yyyy')} {t('contactMessageDetailView.at')} {format(new Date(contact.updated_at), 'h:mm a')}
										</span>
									</div>

									{contact.external_user_id && (
										<div className="flex justify-between items-start">
											<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
												<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
													<svg className="h-2 w-2 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
													</svg>
												</div>
												{t('contactMessageDetailView.userId')}:
											</span>
											<div className="flex-1 flex justify-end ml-2">
												<code className="text-foreground font-medium text-xs text-right">
													{contact.external_user_id}
												</code>
											</div>
										</div>
									)}

									{contact.user_properties && Object.entries(contact.user_properties).map(([key, value]) => (
										<div key={key} className="flex justify-between items-center">
											<span className="text-xs font-medium text-muted-foreground capitalize flex items-center gap-1">
												<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
													<svg className="h-2 w-2 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
													</svg>
												</div>
												{key.replace(/([A-Z])/g, ' $1')}:
											</span>
											<div className="flex-1 flex justify-end ml-2 text-xs">
												<span className="text-foreground font-medium text-right">{String(value)}</span>
											</div>
										</div>
									))}
								</div>
							</div>

							<Separator className="my-2" />
							{contact.user_metadata && (
								<div className="space-y-3">
									<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-orange-100 dark:bg-orange-900 rounded-full">
											<Globe className="h-2.5 w-2.5 text-orange-600 dark:text-orange-400" />
										</div>
										{t('contactMessageDetailView.techContext')}
									</h3>
									<div className="bg-muted/50 p-4 rounded-lg space-y-3">
										{(contact.user_metadata.parsedUA?.os?.name || contact.user_metadata.os) && (
											<div className="flex justify-between items-center">
												<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
													<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
														<Laptop className="h-2 w-2 text-blue-600 dark:text-blue-400" />
													</div>
													{t('contactMessageDetailView.os')}:
												</span>
												<span className="text-xs text-foreground font-medium">
													{contact.user_metadata.parsedUA?.os?.name
														? `${contact.user_metadata.parsedUA.os.name}${contact.user_metadata.parsedUA.os.version ? ` ${contact.user_metadata.parsedUA.os.version}` : ''}`
														: `${contact.user_metadata.os}${contact.user_metadata.osVersion ? ` ${contact.user_metadata.osVersion}` : ''}`
													}
												</span>
											</div>
										)}

										{(contact.user_metadata.parsedUA?.browser?.name || contact.user_metadata.browser) && (
											<div className="flex justify-between items-center">
												<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
													<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
														<Globe className="h-2 w-2 text-blue-600 dark:text-blue-400" />
													</div>
													{t('contactMessageDetailView.browser')}:
												</span>
												<span className="text-xs text-foreground font-medium">
													{contact.user_metadata.parsedUA?.browser?.name
														? `${contact.user_metadata.parsedUA.browser.name}${contact.user_metadata.parsedUA.browser.version ? ` ${contact.user_metadata.parsedUA.browser.version}` : ''}`
														: `${contact.user_metadata.browser}${contact.user_metadata.browserVersion ? ` ${contact.user_metadata.browserVersion}` : ''}`
													}
												</span>
											</div>
										)}

										{(contact.user_metadata.screenWidth && contact.user_metadata.screenHeight) && (
											<div className="flex justify-between items-center">
												<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
													<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
														<Smartphone className="h-2 w-2 text-blue-600 dark:text-blue-400" />
													</div>
													{t('contactMessageDetailView.resolution')}:
												</span>
												<span className="text-xs text-foreground font-medium">
													{contact.user_metadata.screenWidth}x{contact.user_metadata.screenHeight}
												</span>
											</div>
										)}

										{contact.user_metadata.pageUrl && (
											<div className="flex justify-between items-start">
												<span className="text-xs font-medium text-muted-foreground flex items-center gap-1 shrink-0">
													<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
														<svg className="h-2 w-2 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
														</svg>
													</div>
													{t('contactMessageDetailView.pageUrl')}:
												</span>
												<div className="flex-1 flex justify-end ml-2">
													<a
														href={contact.user_metadata.pageUrl}
														target="_blank"
														rel="noopener noreferrer"
														className="text-xs text-primary hover:underline break-all text-right">
														{contact.user_metadata.pageUrl}
													</a>
												</div>
											</div>
										)}

										{contact.user_metadata.userAgent && (
											<div className="flex justify-between items-start">
												<span className="text-xs font-medium text-muted-foreground flex items-center gap-1 shrink-0">
													<div className="h-3 w-3 flex items-center justify-center bg-blue-100 dark:bg-blue-900 rounded-full">
														<svg className="h-2 w-2 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
														</svg>
													</div>
													{t('contactMessageDetailView.userAgent')}:
												</span>
												<div className="flex-1 flex justify-end ml-2">
													<span className="text-xs text-foreground font-medium break-all text-right">
														{contact.user_metadata.userAgent}
													</span>
												</div>
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
